// Loads the reCAPTCHA v3 script once and exposes a helper to mint an
// action-scoped token for a form submission. The token is opaque to the
// client — it must be verified server-side (a Cloud Function calling Google's
// siteverify endpoint) before being trusted, and nothing here does that.
//
// ONE caller's token is actually verified: the failed-login path. login()
// mints an action-scoped token and recordFailedLogin checks it through
// verifyRecaptcha (functions/src/index.ts) before touching any lockout state —
// without that, the callable would be an open "lock out any email" endpoint.
//
// RegisterPage.vue's 'register' token is minted and then goes nowhere: no
// callable receives it, so registration gets the script's passive signals and
// nothing more. Wiring it up means sending it to a callable that verifies it.

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

let scriptPromise: Promise<void> | null = null

function loadScript(siteKey: string): Promise<void> {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

export function useRecaptcha() {
  const siteKey = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY

  async function getToken(action: string): Promise<string | null> {
    if (!siteKey) return null
    await loadScript(siteKey)
    return new Promise((resolve, reject) => {
      window.grecaptcha!.ready(() => {
        // grecaptcha.execute() can throw *synchronously* (e.g. an invalid
        // site key) instead of returning a rejected promise — without this
        // try/catch, that throw happens inside the ready() callback where
        // nothing catches it, so this promise never settles and the caller
        // hangs forever awaiting a token that will never arrive. Wrap it so
        // every failure mode becomes a normal rejection the caller can catch.
        try {
          window.grecaptcha!.execute(siteKey, { action }).then(resolve).catch(reject)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('reCAPTCHA execute() failed'))
        }
      })
    })
  }

  return { getToken }
}
